import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import CompositionBridge from "../../../librettoEditor/CompositionBridge";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import type { FindHighlightsSettings } from "../FindHighlightsSidebar";
import {
    buildEditedTranscriptUnitsForCreateClips,
    type PostAiCreateClipsFn,
} from "../ai/createClips";
import { focusTranscriptWordAtTime } from "../V2TextEditor/transcriptNavigation";
import { buildFindHighlightResultsFromCandidates, buildTranscriptRangeHighlights } from "../findHighlights/utils";
import type { FindHighlightResult } from "../findHighlights/types";

type UseFindHighlightsWorkflowArgs = {
    selectedCompositionId: string | null;
    postAiCreateClips: PostAiCreateClipsFn;
    onShowResults: () => void;
};

export const useFindHighlightsWorkflow = ({
    selectedCompositionId,
    postAiCreateClips,
    onShowResults,
}: UseFindHighlightsWorkflowArgs) => {
    const [isFindingHighlights, setIsFindingHighlights] = useState(false);
    const [findHighlightsDraft, setFindHighlightsDraft] = useState<FindHighlightResult[]>([]);
    const [selectedFindHighlightId, setSelectedFindHighlightId] = useState<string | null>(null);
    const [findHighlightsColor, setFindHighlightsColor] = useState<string | null>(null);
    const [findHighlightsSourceCompositionId, setFindHighlightsSourceCompositionId] = useState<string | null>(null);
    const findHighlightsRunSeqRef = useRef(0);

    const transcriptHighlights = useMemo(() => {
        if (
            !findHighlightsColor ||
            findHighlightsDraft.length === 0 ||
            !selectedCompositionId ||
            findHighlightsSourceCompositionId !== selectedCompositionId
        ) {
            return [];
        }

        return buildTranscriptRangeHighlights({
            highlights: findHighlightsDraft,
            color: findHighlightsColor,
        });
    }, [findHighlightsColor, findHighlightsDraft, findHighlightsSourceCompositionId, selectedCompositionId]);

    const resetFindHighlightsReview = useCallback((options?: { clearSelection?: boolean }) => {
        findHighlightsRunSeqRef.current += 1;
        setIsFindingHighlights(false);
        setFindHighlightsDraft([]);
        setSelectedFindHighlightId(null);
        setFindHighlightsColor(null);
        setFindHighlightsSourceCompositionId(null);
        if (options?.clearSelection ?? true) {
            ApplicationStore.setSelectedTranscriptTimelineRange(null);
            ApplicationStore.requestTranscriptTimelineSelection(null);
        }
    }, []);

    const focusFindHighlight = useCallback((highlight: FindHighlightResult | null | undefined) => {
        const startMs = Number(highlight?.startMs ?? NaN);
        const endMs = Number(highlight?.endMs ?? NaN);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return;

        const startSec = Math.max(0, startMs / 1000);
        const endSec = Math.max(startSec, endMs / 1000);
        ApplicationStore.setSelectedTranscriptTimelineRange({ start: startSec, end: endSec });
        ApplicationStore.requestTranscriptTimelineSelection({ start: startSec, end: endSec });
        focusTranscriptWordAtTime(startSec);
        void CompositionService.seek(startSec + 0.001);
    }, []);

    const handleSelectFindHighlight = useCallback((highlightId: string) => {
        const nextHighlight = findHighlightsDraft.find((highlight) => highlight.id === highlightId) ?? null;
        if (!nextHighlight) return;
        setSelectedFindHighlightId(nextHighlight.id);
        focusFindHighlight(nextHighlight);
    }, [findHighlightsDraft, focusFindHighlight]);

    const handleFindHighlightsSubmit = useCallback(async (settings: FindHighlightsSettings) => {
        if (!selectedCompositionId) return;

        const runSeq = findHighlightsRunSeqRef.current + 1;
        findHighlightsRunSeqRef.current = runSeq;
        const sourceCompositionId = selectedCompositionId;

        setIsFindingHighlights(true);
        setFindHighlightsDraft([]);
        setSelectedFindHighlightId(null);
        setFindHighlightsColor(settings.highlightColor);
        setFindHighlightsSourceCompositionId(sourceCompositionId);
        ApplicationStore.setSelectedTranscriptTimelineRange(null);
        ApplicationStore.requestTranscriptTimelineSelection(null);

        try {
            await CompositionBridge.syncScriptLayerStore();
            const transcriptUnits = buildEditedTranscriptUnitsForCreateClips();
            if (transcriptUnits.length === 0) {
                throw new Error("Transcript content is not ready yet.");
            }

            const response = await postAiCreateClips({
                compositionId: sourceCompositionId,
                maxClips: settings.highlightCount,
                clipDurationSec: 20,
                topicPrompt: settings.topic || undefined,
                transcriptUnits,
            });

            if (findHighlightsRunSeqRef.current !== runSeq) return;

            const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
            const highlights = buildFindHighlightResultsFromCandidates({
                candidates,
                transcriptUnits,
            });
            if (highlights.length === 0) {
                throw new Error("No highlights were returned.");
            }

            setFindHighlightsDraft(highlights);
            setSelectedFindHighlightId(highlights[0]?.id ?? null);
            onShowResults();
            focusFindHighlight(highlights[0]);
        } catch (error) {
            console.error("[V2Editor] Failed to find highlights", error);
            const message = String((error as any)?.message ?? "Failed to find highlights.").trim();
            if (message) {
                window.alert(message);
            }
            resetFindHighlightsReview();
        } finally {
            if (findHighlightsRunSeqRef.current === runSeq) {
                setIsFindingHighlights(false);
            }
        }
    }, [focusFindHighlight, onShowResults, postAiCreateClips, resetFindHighlightsReview, selectedCompositionId]);

    useEffect(() => {
        resetFindHighlightsReview();
    }, [resetFindHighlightsReview, selectedCompositionId]);

    return {
        isFindingHighlights,
        findHighlightsDraft,
        selectedFindHighlightId,
        transcriptHighlights,
        hasFindHighlightsDraft: findHighlightsDraft.length > 0,
        handleFindHighlightsSubmit,
        handleSelectFindHighlight,
        resetFindHighlightsReview,
    };
};
